import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents, CartUpdateEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';
import { isMobileBreakpoint } from '@theme/utilities';

const WRAPPED_CART_DRAWER_EVENT = 'custom:event:cart-drawer';

/** @type {Promise<void> | null} */
let wrappedCartRefreshPromise = null;

/**
 * Re-fetches cart state and cart sections after Wrapped mutates the cart.
 */
async function refreshCartFromWrapped() {
  if (wrappedCartRefreshPromise) return wrappedCartRefreshPromise;

  wrappedCartRefreshPromise = (async () => {
    const sectionIds = [
      ...new Set(
        [...document.querySelectorAll('cart-items-component[data-section-id]')]
          .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
          .filter(Boolean)
      ),
    ];

    const cartResponse = await fetch('/cart.js');
    if (!cartResponse.ok) throw new Error('Failed to fetch cart');
    const cart = await cartResponse.json();

    /** @type {Record<string, string>} */
    const sections = {};
    await Promise.all(
      sectionIds.map(async (sectionId) => {
        sections[sectionId] = await sectionRenderer.getSectionHTML(sectionId, false);
      })
    );

    document.dispatchEvent(
      new CartUpdateEvent(cart, 'wrapped-app', {
        itemCount: cart.item_count,
        source: 'wrapped-app',
        sections,
      })
    );
  })()
    .catch((error) => {
      console.error('Failed to refresh cart after Wrapped update:', error);
    })
    .finally(() => {
      wrappedCartRefreshPromise = null;
    });

  return wrappedCartRefreshPromise;
}

document.addEventListener(WRAPPED_CART_DRAWER_EVENT, refreshCartFromWrapped);

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {HTMLElement | null} */
  #buyBoxToRestore = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartUpdate, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#trackOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener(DialogCloseEvent.eventName, this.#restoreBuyBox);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }

    const url = new URL(window.location.href);
    if (url.searchParams.get('cart') === 'open') {
      url.searchParams.delete('cart');
      history.replaceState(history.state, '', url);
      this.open();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#trackOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener(DialogCloseEvent.eventName, this.#restoreBuyBox);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #trackOpen = () => {
    this.dispatchEvent(new CustomEvent('cart-drawer:open', { bubbles: true }));
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number }, sourceId?: string }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  /**
   * @param {HTMLElement | null} [buyBox] - Open buy box to retain beneath this drawer.
   */
  open(buyBox = null) {
    if (buyBox?.isConnected && typeof buyBox.openPurchaseSurface === 'function') {
      this.#buyBoxToRestore = buyBox;
      buyBox.suspendForCartDrawer?.();
    }
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #restoreBuyBox = () => {
    const buyBox = this.#buyBoxToRestore;
    this.#buyBoxToRestore = null;

    if (buyBox?.isConnected) {
      buyBox.resumeAfterCartDrawer?.();
    }
  };

  #updateStickyState = () => {
    if (!this.isConnected) return;

    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  };
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
